<div class="pa-wrap">

  <div class="pa-empty" *ngIf="!_isSuperAdmin && !loading">
    Only a super-admin can manage customer portal access.
  </div>

  <ng-container *ngIf="_isSuperAdmin">
    <div class="pa-toolbar">
      <p class="pa-note">
        Give a <strong>customer contact</strong> their own login to the
        <strong>customer portal</strong>, where they manage their own employees —
        add new hires, deactivate leavers, and see what has been bought.
        Access is granted per <strong>kundekort</strong>: the portal shows every employee
        in the groups linked to that customer.
      </p>
      <span class="pa-spacer"></span>
      <button mat-flat-button color="primary" (click)="openForm()">
        <mat-icon>person_add</mat-icon> Grant portal access
      </button>
    </div>

    <!-- new account form ------------------------------------------------- -->
    <div class="pa-card" *ngIf="showForm">
      <h3 class="pa-card-title">{{ editingUid ? 'Edit portal access — ' + form.email : 'New portal account' }}</h3>

      <div class="pa-form-row">
        <mat-form-field appearance="outline" class="pa-field" *ngIf="!editingUid">
          <mat-label>Email</mat-label>
          <input matInput [(ngModel)]="form.email" type="email" placeholder="name@customer.no" autocomplete="off">
          <mat-hint>Must not be an existing Frigg admin account — use the person's work email.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline" class="pa-field">
          <mat-label>Name</mat-label>
          <input matInput [(ngModel)]="form.name" placeholder="Full name" autocomplete="off">
        </mat-form-field>

        <mat-form-field appearance="outline" class="pa-field">
          <mat-label>Role</mat-label>
          <mat-select [(ngModel)]="form.role">
            <mat-option *ngFor="let r of roles" [value]="r.value">{{ r.label }} — {{ r.hint }}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>

      <div class="pa-picker">
        <div class="pa-picker-head">
          <strong>Customers</strong>
          <span class="pa-chip" [class.pa-chip-warn]="!form.customerIds.length">
            {{ form.customerIds.length }} selected
          </span>
          <span class="pa-spacer"></span>
          <mat-form-field appearance="outline" class="pa-search">
            <mat-icon matPrefix>search</mat-icon>
            <input matInput [(ngModel)]="customerSearch" placeholder="Search customer or org no…">
            <button mat-icon-button matSuffix *ngIf="customerSearch" (click)="customerSearch = ''">
              <mat-icon>close</mat-icon>
            </button>
          </mat-form-field>
        </div>

        <div class="pa-picker-list">
          <label class="pa-picker-item" *ngFor="let c of filteredCustomers"
                 [class.pa-picked]="isPicked(c.customerId)">
            <input type="checkbox" [checked]="isPicked(c.customerId)" (change)="toggleCustomer(c.customerId)">
            <span class="pa-picker-name">{{ c.name }}</span>
            <span class="pa-muted" *ngIf="c.orgId">{{ c.orgId }}</span>
            <span class="pa-spacer"></span>
            <span class="pa-chip" [class.pa-chip-warn]="!groupCountByCustomer[c.customerId]">
              {{ groupCountByCustomer[c.customerId] || 0 }} groups
            </span>
          </label>
          <div class="pa-empty" *ngIf="!filteredCustomers.length">No customer matches that search.</div>
        </div>
        <p class="pa-note pa-warn" *ngIf="form.customerIds.length && hasNoGroups(form)">
          <mat-icon>warning</mat-icon>
          None of the selected customers has a group linked to it, so this account would sign in to an
          empty portal. Link their groups on <strong>Customer Mapping</strong> first.
        </p>
      </div>

      <div class="pa-form-actions">
        <button mat-button (click)="cancelForm()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" (click)="save()" [disabled]="saving">
          {{ saving ? 'Saving…' : (editingUid ? 'Save changes' : 'Create and send invite') }}
        </button>
      </div>
      <p class="pa-note" *ngIf="!editingUid">
        No password is set here. The account is created and the person gets an email with a link
        to choose their own password.
      </p>
      <p class="pa-note" *ngIf="editingUid">
        Removing a customer takes effect on their next request — they immediately stop seeing
        that company's employees. Their password and sign-in are unaffected.
      </p>
    </div>

    <!-- existing accounts ------------------------------------------------ -->
    <div class="pa-toolbar">
      <mat-form-field appearance="outline" class="pa-search">
        <mat-icon matPrefix>search</mat-icon>
        <input matInput [(ngModel)]="search" placeholder="Search name, email or customer…">
        <button mat-icon-button matSuffix *ngIf="search" (click)="search = ''">
          <mat-icon>close</mat-icon>
        </button>
      </mat-form-field>
      <span class="pa-spacer"></span>
      <span class="pa-chip">{{ users.length }} account(s)</span>
      <button mat-icon-button (click)="reload()" matTooltip="Reload">
        <mat-icon>refresh</mat-icon>
      </button>
    </div>

    <div class="pa-empty" *ngIf="loading">Loading…</div>
    <div class="pa-empty pa-error" *ngIf="error">{{ error }}</div>

    <div class="pa-table-wrap" *ngIf="!loading && !error">
      <table class="pa-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Customers</th>
            <th>Role</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let u of filteredUsers" [class.pa-row-off]="!u.status">
            <td>{{ u.name || '—' }}</td>
            <td>{{ u.email }}</td>
            <td class="pa-cust-cell">
              <span class="pa-cust-chip" *ngFor="let cid of u.customerIds">{{ customerName(cid) }}</span>
              <span class="pa-chip pa-chip-warn" *ngIf="hasNoGroups(u)"
                    matTooltip="No group is linked to this customer, so the portal will be empty. Fix on Customer Mapping.">
                no groups
              </span>
            </td>
            <td>
              <mat-select [value]="u.role" (selectionChange)="setRole(u, $event.value)"
                          [disabled]="busy[u.uid]" class="pa-role-select">
                <mat-option *ngFor="let r of roles" [value]="r.value">{{ r.label }}</mat-option>
              </mat-select>
            </td>
            <td>
              <span class="pa-chip pa-chip-ok" *ngIf="u.status">Active</span>
              <span class="pa-chip pa-chip-off" *ngIf="!u.status">Revoked</span>
            </td>
            <td class="pa-actions">
              <button mat-icon-button (click)="editUser(u)" [disabled]="busy[u.uid]"
                      matTooltip="Edit customers, role and name">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button (click)="resendInvite(u)" [disabled]="busy[u.uid]"
                      matTooltip="Email a new password link">
                <mat-icon>mail</mat-icon>
              </button>
              <button mat-icon-button *ngIf="u.status" (click)="setStatus(u, false)" [disabled]="busy[u.uid]"
                      matTooltip="Revoke access — signs them out immediately">
                <mat-icon>block</mat-icon>
              </button>
              <button mat-icon-button *ngIf="!u.status" (click)="setStatus(u, true)" [disabled]="busy[u.uid]"
                      matTooltip="Restore access">
                <mat-icon>undo</mat-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pa-empty" *ngIf="!filteredUsers.length">
        No portal accounts yet. Use <strong>Grant portal access</strong> to create the first one.
      </div>
    </div>
  </ng-container>
</div>
